import { find, map } from 'lodash'

import { API } from '@/constants'
import { axiosServer as axios } from '@/base/axios'

import { WPLink, WPPageData } from '@/types'

export const PostType = {
  POST: 'post'
}

export const normalizeWPLink = (
  type: string,
  link: WPLink,
  labels: Record<string, string>
) => {
  const linkLabelsMap = {
    [PostType.POST]: labels?.read_more
  }

  const title = linkLabelsMap[type]

  return (
    (type && link
      ? {
          title: title || labels?.read_more,
          url: link,
          target: ''
        }
      : link) || null
  )
}

export const normalizeWPTaxonomies = (type: string, taxonomies: any[]) => {
  const taxonomiesMap = {
    [PostType.POST]: 'category'
  }

  const taxonomy = taxonomiesMap[type] as any

  return type && taxonomy
    ? map(taxonomies ? taxonomies[taxonomy] : [], 'name').join(', ')
    : null
}

export const normalizePostsCollection = (
  collection: any[],
  { labels }: any
) => {
  return map(
    collection,
    ({
      type,
      color,
      overline,
      image,
      featured_image,
      title_component,
      title_variant,
      title,
      paragraph,
      content,
      taxonomies,
      link
    }) => {
      return {
        type: type || 'post',
        color: color || '#7d3bed',
        image: featured_image || image || null,
        overline: overline || normalizeWPTaxonomies(type, taxonomies),
        title_component: title_component || 'h6',
        title_variant: title_variant || 'h6',
        title: title?.rendered || title || null,
        paragraph: content?.rendered || paragraph || null,
        link: normalizeWPLink(type, link, labels)
      }
    }
  )
}

export const normalizeRelationalSection = (section: any, pageData: any) => {
  const type = section.collection_type
  const collection = section[type] || section.collection

  return {
    ...section,
    collection: normalizePostsCollection(collection, pageData)
  }
}

export const normalizeRelationalArchive = async (
  section: any,
  pageData: any
) => ({
  ...(await normalizeRelationalSection(section, pageData))
})

export const normalizeYoastGraph = (pageData: any) => {
  const schema = pageData.yoast_head_json.schema
  const breadcrumbs = find(schema['@graph'], {
    '@type': 'BreadcrumbList'
  })?.itemListElement

  pageData.type === 'post' &&
    breadcrumbs.splice(
      1,
      1,
      ...[
        {
          '@type': 'ListItem',
          item: '/newsroom/',
          name: 'Newsroom',
          position: 2
        },
        {
          '@type': 'ListItem',
          // item: '/newsroom/news/',
          name: 'News',
          position: 3
        }
      ]
    )

  return breadcrumbs
}

export const normalizeHeroTitleSection = async (
  section: any,
  pageData: any
) => ({
  breadcrumbs: normalizeYoastGraph(pageData),
  ...section
})

export const resolveGravityFieldsFromFormId = async (section: any) => {
  const {
    data: { fields, button }
  } = await axios.get(`${API.GRAVITY_FORM}/${section.form.form_id}/`)

  return {
    ...section,
    form: {
      ...section.form,
      submit: button.text,
      fields
    }
  }
}

export const mapPageStaticProps = async ({ acf, ...pageData }: WPPageData) => {
  return {
    ...pageData,
    acf: {
      ...acf,
      builder: await Promise.all(
        map(acf.builder, async (section: any) =>
          section.acf_fc_layout === 'hero_title'
            ? normalizeHeroTitleSection(section, pageData)
            : section.acf_fc_layout === 'image_cards'
              ? normalizeRelationalSection(section, pageData)
              : section.acf_fc_layout === 'archive_post_types'
                ? normalizeRelationalArchive(section, pageData)
                : section.acf_fc_layout === 'newsletter'
                  ? resolveGravityFieldsFromFormId(section)
                  : section
        )
      )
    }
  }
}

export const mapOptionsDataProps = async (optionsData: any) => {
  const {
    data: { fields, button }
  } = await axios.get(
    `${API.GRAVITY_FORM}/${optionsData.advisor_banner.form_id}/`
  )

  return {
    ...optionsData,
    advisor_banner: {
      ...optionsData.advisor_banner,
      submit: button.text,
      fields
    }
  }
}
